import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { modalStyles } from '../styles/modalStyles';
import { API_ENDPOINTS, API_CONFIG, STORAGE_KEYS } from '../helpers/constants';
import { useAuth } from '../contexts/AuthContext';

interface UserInvite {
  email: string;
  company_id: string;
  role: string;
}

interface BulkInviteRequest {
  users: UserInvite[];
  user_id: string;
  platform_name: string;
}

interface BulkInviteResponse {
  message: string;
  total_invited: number;
  successful_invitations: Array<{
    message: string;
    invite_id: string;
    email_id: string;
    registration_link: string;
    expires_at: string;
  }>;
  failed_invitations: Array<{
    email_id: string;
    reason: string;
  }>;
}

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  onSuccess?: () => void;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ open, onClose, companyId, onSuccess }) => {
  const [users, setUsers] = useState<UserInvite[]>([
    { email: '', company_id: companyId, role: 'user' }
  ]);
  const { user: currentUserData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const errorAlertRef = useRef<HTMLDivElement>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);

  // Shared validation: count/show invite only for valid email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleAddUser = () => {
    setUsers([...users, { email: '', company_id: companyId, role: 'user' }]);
  };

  const handleRemoveUser = (index: number) => {
    if (users.length > 1) {
      setUsers(users.filter((_, i) => i !== index));
    }
  };

  const handleUserChange = (index: number, field: keyof UserInvite, value: string) => {
    const newUsers = [...users];
    newUsers[index][field] = value;
    setUsers(newUsers);
  };

  const handleSubmit = async () => {
    // Validate all fields are filled
    const isValid = users.every(user => user.email.trim() && user.role);
    
    if (!isValid) {
      setError('Please fill in all fields for all users');
      return;
    }

    // Validate email format (use shared regex)
    const hasValidEmails = users.every(user => emailRegex.test(user.email));
    
    if (!hasValidEmails) {
      setError('Please enter valid email addresses for all users');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Get user_id from localStorage as fallback if context doesn't have it
      const storedUserData = (() => {
        try {
          const raw = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
          return raw ? JSON.parse(raw) : null;
        } catch {
          return null;
        }
      })();
      
      const userId = currentUserData?.id || storedUserData?.user_id || '';
      
      const requestData: BulkInviteRequest = {
        users: users.map(user => ({
          ...user,
          company_id: companyId
        })),
        user_id: userId,
        platform_name: "Prism 7"
      };

      const token = localStorage.getItem('refreshToken') || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.USER_AUTH.BULK_INVITE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: BulkInviteResponse = await response.json();
      
      // Build error messages from failed invitations
      const errorMessages: string[] = [];
      if (result.failed_invitations && result.failed_invitations.length > 0) {
        result.failed_invitations.forEach((failed) => {
          if (failed.email_id && failed.reason) {
            errorMessages.push(`${failed.email_id}: ${failed.reason}`);
          }
        });
      }
      
      if (result.successful_invitations.length > 0) {
        // Some users were invited successfully
        const successMsg = `Successfully invited ${result.successful_invitations.length} user${result.successful_invitations.length > 1 ? 's' : ''}!`;
        
        // If there are also failures, show both success and error messages
        if (errorMessages.length > 0) {
          setSuccess(successMsg);
          setError(errorMessages.join('; '));
        } else {
          setSuccess(successMsg);
        }
        
        // Call onSuccess callback to refresh the users list
        if (onSuccess) {
          onSuccess();
        }
        
        // Reset form after successful submission (only if all succeeded)
        if (errorMessages.length === 0) {
          setTimeout(() => {
            setUsers([{ email: '', company_id: companyId, role: 'user' }]);
            onClose();
          }, 2000);
        }
      } else {
        // No users were invited successfully - show specific error messages
        if (errorMessages.length > 0) {
          setError(errorMessages.join('; '));
        } else {
          setError(result.message || 'No users were invited successfully');
        }
      }
    } catch (err) {
      console.error('Error inviting users:', err);
      setError('Failed to invite users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Scroll to top when error appears
  useEffect(() => {
    if (error && dialogContentRef.current) {
      // Small delay to ensure the Alert is rendered
      setTimeout(() => {
        if (dialogContentRef.current) {
          // Scroll to the top of the dialog content
          dialogContentRef.current.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [error]);

  const handleClose = () => {
    if (!loading) {
      setUsers([{ email: '', company_id: companyId, role: 'user' }]);
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  // Count only rows with valid email format (for button text and disable state)
  const usersWithValidEmailCount = users.filter(u => emailRegex.test(u.email.trim())).length;

  return (
         <Dialog 
       open={open} 
       onClose={(event, reason) => {
         if (reason === 'backdropClick') {
           return; // Prevent closing when clicking outside
         }
         handleClose();
       }}
       maxWidth="sm"
       fullWidth
       disableEscapeKeyDown
       PaperProps={{
         sx: {
           borderRadius: 2,
           boxShadow: 3,
         }
       }}
     >
            <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Add Users
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent 
        ref={dialogContentRef}
        sx={{ pt: 1, maxHeight: '70vh', overflow: 'auto' }}
      >
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Invite multiple users to join your organization by providing their email and role. Each user will receive an email invitation.
          </Typography>
        </Box>

        {error && (
          <Box ref={errorAlertRef} sx={{ mb: 2 }}>
            <Alert severity="error">
              {error}
            </Alert>
          </Box>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {users.map((user, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-start',
                }}
              >
                <TextField
                  label="Email"
                  type="email"
                  value={user.email}
                  onChange={(e) => handleUserChange(index, 'email', e.target.value)}
                  fullWidth
                  size="small"
                  disabled={loading}
                  required
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={user.role}
                    label="Role"
                    onChange={(e) => handleUserChange(index, 'role', e.target.value)}
                    disabled={loading}
                  >
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
                {users.length > 1 && (
                  <IconButton
                    onClick={() => handleRemoveUser(index)}
                    disabled={loading}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              onClick={handleAddUser}
              disabled={loading}
              sx={{
                color: 'primary.main',
                textTransform: 'none',
                p: 0,
                minWidth: 'auto',
                '&:hover': { backgroundColor: 'transparent' }
              }}
            >
              + Add another user
            </Button>
          </Box>
       </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ 
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || usersWithValidEmailCount === 0}
          variant="contained"
          color="primary"
          sx={{ 
            backgroundColor: 'primary.main',
            color: '#fff',
            '&:hover': { backgroundColor: 'primary.dark', color: '#fff' },
            '&:disabled': { backgroundColor: 'primary.light' }
          }}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? 'Inviting Users...' : usersWithValidEmailCount === 0 ? 'Invite' : `Invite ${usersWithValidEmailCount} User${usersWithValidEmailCount > 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserModal;
